import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Breadcrumbs, Card, CardContent, Grid, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";

const USER_TYP_ADMIN = 1;

// Developer sekcia – viditeľná len pre administrátorov (type: 1)
const DEVELOPER_BOXES = [
  {
    id: "api-docs",
    icon: MenuBookIcon,
    titleKey: "pages.systemSettings.apiDocsTitle",
    descriptionKey: "pages.systemSettings.apiDocsDescription",
    path: "/dsok/system-settings/developer/api-docs",
  },
];

const SETTING_BOXES = [
  {
    id: "customer-settings",
    icon: SettingsIcon,
    titleKey: "pages.systemSettings.customerSettingsTitle",
    descriptionKey: "pages.systemSettings.customerSettingsDescription",
    path: "/dsok/system-settings/new-customer-settings",
  },
  {
    id: "billing",
    icon: ReceiptLongIcon,
    titleKey: "pages.systemSettings.billingTitle",
    descriptionKey: "pages.systemSettings.billingDescription",
    path: "/dsok/system-settings/billing",
  },
];

function SettingBox({ icon: Icon, titleKey, descriptionKey, path }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleClick = path ? () => navigate(path) : undefined;

  return (
    <Card
      variant="outlined"
      sx={{
        height: "100%",
        ...(handleClick && {
          cursor: "pointer",
          transition: "box-shadow 0.2s, background-color 0.2s",
          "&:hover": {
            boxShadow: 1,
            bgcolor: "action.hover",
          },
        }),
      }}
      onClick={handleClick}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "action.hover",
              flexShrink: 0,
            }}
          >
            {Icon && <Icon sx={{ fontSize: 28, color: "primary.main" }} />}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t(titleKey)}
            </Typography>
            {descriptionKey && (
              <Typography variant="body2" color="text.secondary">
                {t(descriptionKey)}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SystemSettingsPage() {
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const isAdmin = Number(authUser?.type ?? authUser?.typ) === USER_TYP_ADMIN;

  return (
    <Box sx={{ px: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{ flex: "1 1 auto", minWidth: 0 }}
        >
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}>
            {t("sidebar.menuItem.home")}
          </Link>
          <Typography color="text.primary">
            {t("pages.systemSettings.breadcrumb", { defaultValue: "Nastavenia" })}
          </Typography>
        </Breadcrumbs>
      </Box>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t("pages.systemSettings.title", { defaultValue: "Nastavenia systému" })}
      </Typography>

      <Grid container spacing={2}>
        {[...SETTING_BOXES, ...(isAdmin ? DEVELOPER_BOXES : [])].map((box) => (
          <Grid item xs={12} sm={6} md={3} key={box.id}>
            <SettingBox
              icon={box.icon}
              titleKey={box.titleKey}
              descriptionKey={box.descriptionKey}
              path={box.path}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
